/**
 * ARIA radio pattern for button-based radio groups: the group is one Tab
 * stop (the checked radio, else the first) and the arrow keys, Home and End
 * move focus and select, wrapping at the ends.
 */
import type { KeyboardEvent } from 'react';

/** Tab index for radio `index` of a group where `checkedIndex` is selected (-1: none). */
export function radioTabIndex(index: number, checkedIndex: number): 0 | -1 {
  return index === (checkedIndex < 0 ? 0 : checkedIndex) ? 0 : -1;
}

/** onKeyDown for each radio: moves to and clicks the next radio in its group. */
export function radioKeyDown(e: KeyboardEvent<HTMLElement>): void {
  const group = e.currentTarget.closest('[role="radiogroup"]');
  if (!group) return;
  const radios = [...group.querySelectorAll<HTMLElement>('[role="radio"]')].filter(
    (r) => !r.hasAttribute('disabled'),
  );
  const i = radios.indexOf(e.currentTarget);
  if (i < 0 || radios.length === 0) return;
  let to: number;
  switch (e.key) {
    case 'ArrowRight':
    case 'ArrowDown':
      to = (i + 1) % radios.length;
      break;
    case 'ArrowLeft':
    case 'ArrowUp':
      to = (i - 1 + radios.length) % radios.length;
      break;
    case 'Home':
      to = 0;
      break;
    case 'End':
      to = radios.length - 1;
      break;
    default:
      return;
  }
  e.preventDefault();
  const target = radios[to];
  if (!target) return;
  target.focus();
  target.click();
}
